import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useRole } from "./RoleContext";

export interface Todo {
  id: string;
  text: string;
  desc?: string;
  done: boolean;
  priority: "high" | "medium" | "low";
  source?: "role" | "event" | "manual";
  tag?: string;
}

interface TodoContextValue {
  todos: Todo[];
  toggle: (id: string) => void;
  addTodos: (items: Omit<Todo, "id" | "done">[]) => void;
  clearDone: () => void;
}

const TodoContext = createContext<TodoContextValue | null>(null);

const ROLE_TODOS: Record<string, Omit<Todo, "id" | "done" | "source">[]> = {
  CEO: [
    { text: "DarkNova MV 예산안 8,000만원 최종 결재 서명", desc: "재무팀이 준비한 DarkNova MV 제작비 8,000만원 예산안을 검토하고 CEO 서명란에 날인. 항목별 단가·견적서 첨부 여부 확인 필수.", priority: "high", tag: "결재" },
    { text: "루나&스타즈 전속계약서 법무 검토 완료 후 서명", desc: "법무팀이 최종 수정한 전속계약서 v3 수령 후 주요 변경 조항(수익배분·전속기간) 재확인 및 서명. 서명본 스캔 후 법무팀 보관.", priority: "high", tag: "계약" },
    { text: "케이팝파트너스 투자 미팅 준비 자료 작성", desc: "투자사 IR 미팅용 회사 소개 덱 최신화. 최근 6개월 실적(음원 수익·SNS 지표)·향후 로드맵·투자금 활용 계획 포함. 재무팀 데이터 요청.", priority: "high", tag: "투자" },
    { text: "분기 이사회 보고자료 초안 재무팀에서 받기", desc: "재무팀에 이번 분기 손익계산서·현금흐름표·KPI 달성 현황 초안 요청. 수령 후 CEO 코멘트 추가해 다음 주 이사회 전 배포.", priority: "medium", tag: "경영" },
    { text: "신인 아티스트 오디션 2차 심사 일정 확인", desc: "A&R팀이 준비한 오디션 2차 심사(서울·부산) 일정 확인 및 캘린더 등록. 심사위원 구성·평가 기준표 사전 수령.", priority: "medium", tag: "인사" },
    { text: "경쟁사 블랙스타엔터 동향 분석 보고서 검토", desc: "마케팅팀이 작성한 블랙스타엔터 최근 발매 동향·팬덤 규모·계약 소식 등 분석 보고서 검토 후 전략 방향 코멘트.", priority: "medium", tag: "전략" },
    { text: "전 직원 분기 성과 리뷰 일정 공지", desc: "각 팀장과 1:1 성과 리뷰 일정 조율 (마케팅·A&R·재무·법무·공연). 인사팀에 성과 평가 양식 배포 요청.", priority: "low", tag: "인사" },
  ],
  AR: [
    { text: "루나&스타즈 1st EP 데모곡 10곡 중 타이틀 최종 선곡", desc: "작곡가별 데모 10곡을 다시 청취하고 타이틀·서브·인트로 트랙 확정. 선곡 결과를 CEO·마케팅팀과 공유 후 최종 승인 요청.", priority: "high", tag: "앨범" },
    { text: "작곡가 김태현에게 수정 피드백 전달 (브릿지 수정)", desc: "3번 트랙 브릿지 멜로디가 전체 무드와 이질감. 후기 R&B 스타일로 전환 + 코러스 전 4마디 에너지 증폭 방향으로 메일 피드백 전달.", priority: "high", tag: "A&R" },
    { text: "신인 오디션 지원서 1차 서류 검토 20건 완료", desc: "이번 주 접수된 오디션 지원서 20건 중 보컬 샘플 파일 청취 및 기본 조건(나이·소속 없음) 확인. 통과자 2차 심사 안내 메일 발송.", priority: "high", tag: "발굴" },
    { text: "DarkNova 정규 1집 콘셉트 무드보드 수정본 마케팅팀 공유", desc: "무드보드 v2 (다크 사이버펑크 → 네온 고딕으로 콘셉트 전환) 완성 후 마케팅팀에 공유. 커버 디자인 외주 방향성 결정을 위해 이번 주 내 공유 필요.", priority: "medium", tag: "기획" },
    { text: "해외 작곡가 Peter Kim 컨택 결과 정리 및 보고", desc: "Peter Kim 측 에이전시 답변 수령. 작곡비·저작권 배분 조건 정리해 CEO에게 보고. 계약 조건이 합리적이면 법무팀에 계약서 검토 의뢰.", priority: "medium", tag: "A&R" },
    { text: "유통사 발매 일정 조율 메일 회신 (스카이뮤직)", desc: "스카이뮤직 담당자 박수정 씨가 4/15 vs 4/22 발매일 선택을 요청. 4/22로 확정 후 회신. 유통 계약서 부속합의서에 날짜 반영 확인.", priority: "medium", tag: "유통" },
    { text: "재무팀에 데모 수급 예산 500만원 집행 요청", desc: "이번 달 외주 작곡가 데모 수급 비용 500만원 집행 기안서 작성 후 재무팀에 제출. 작곡가별 단가표 첨부 필요.", priority: "low", tag: "예산" },
  ],
  Finance: [
    { text: "이번 달 부가세 신고 자료 세무사 이선생님께 전달", desc: "이번 달 매출세금계산서·매입세금계산서 전량 취합 후 엑셀 정리. 세무사 이선생님 이메일로 발송 (마감일 15일 이전 도착 필수).", priority: "high", tag: "세금" },
    { text: "DarkNova MV 예산안 적정성 검토 후 CEO 결재 상신", desc: "항목별 단가 시장가 비교 (촬영 장소·배우·후반작업). 불필요 항목 삭제 후 총액 7,800만원으로 조정 의견 추가해 CEO 결재 상신.", priority: "high", tag: "예산" },
    { text: "A&R팀 데모 수급 예산 500만원 승인 검토", desc: "A&R팀 기안서 수령 후 작곡가별 단가표 시장가 적정성 검토. 전월 동일 항목 집행 내역과 비교. 승인 후 법인카드 결제 한도 확인.", priority: "medium", tag: "예산" },
    { text: "이번 달 마케팅 집행 내역 대비 실적 정산", desc: "마케팅팀이 집행한 광고비·인플루언서 비용·인쇄물 총액 vs 예산 비교. 초과분 발생 시 원인 파악 후 다음 달 예산 조정 반영.", priority: "medium", tag: "정산" },
    { text: "분기 재무제표 초안 작성 및 내부 검토", desc: "손익계산서·재무상태표·현금흐름표 Q1 데이터 기반 초안 작성. CEO 이사회 보고 전 내부 검토 완료 목표.", priority: "medium", tag: "회계" },
    { text: "스튜디오 임대료 자동 이체 확인 (월말)", desc: "을지로 스튜디오 임대료 380만원 자동이체 처리 확인. 미처리 시 즉시 수동 이체. 영수증 수령 후 회계 처리.", priority: "medium", tag: "관리비" },
    { text: "외주 디자이너 YoonArt 발주서 최종 정산 처리", desc: "YoonArt 최종 납품물(루나&스타즈 앨범 커버 디자인) 검수 완료 후 발주서 기준 용역비 250만원 지급 처리. 세금계산서 수령 확인.", priority: "low", tag: "정산" },
  ],
  Marketing: [
    { text: "루나&스타즈 봄 시즌 SNS 콘텐츠 캘린더 2주분 작성", desc: "인스타그램·X(트위터)·틱톡 채널별 게시 유형(리얼·포토·쇼츠) 및 게시 시간 설정. 2주치(14일) 초안 완성 후 A&R팀 사진 자료 요청.", priority: "high", tag: "SNS" },
    { text: "아이돌차트 김민준 기자 인터뷰 대응 자료 준비", desc: "루나&스타즈 최근 바이럴 현황·팬덤 규모·신보 발매 계획 관련 기자 Q&A 답변 초안 작성. CEO 확인 후 인터뷰 당일 전달.", priority: "high", tag: "언론" },
    { text: "DarkNova 티저 영상 유튜브 업로드 일정 재무팀과 예산 협의", desc: "티저 제작비 견적 800만원 중 300만원이 예산 초과 상태. 재무팀과 협의해 300만원 추가 승인 받거나 제작 범위 조정 결정.", priority: "high", tag: "광고" },
    { text: "음원 차트 주간 동향 보고서 CEO에게 제출", desc: "멜론·지니·벅스 주간 TOP100 변동 현황 + 자사 아티스트 순위 분석. 경쟁사 신보 출시 영향 분석 포함해 CEO에게 제출.", priority: "medium", tag: "보고" },
    { text: "경쟁사 블랙스타엔터 최근 캠페인 분석 리포트 작성", desc: "블랙스타엔터 신보 마케팅 채널 전략·예산 규모 추정·팬 반응 분석. 자사 전략과 차별화 포인트 도출 후 CEO·전략팀에 공유.", priority: "medium", tag: "분석" },
    { text: "인플루언서 협업 계약서 법무팀 검토 의뢰", desc: "틱톡 인플루언서 @dancewith.luna 협업 계약서 초안 작성 완료. 독점 조항·콘텐츠 소유권·수익배분 조항 법무팀 검토 요청.", priority: "medium", tag: "계약" },
    { text: "팬덤 커뮤니티 반응 모니터링 리포트 정리", desc: "팬카페·디시인사이드·트위터 주요 게시글·댓글 감성 분석. 부정 반응 원인 추적 및 핵심 팬 의견 요약 후 주간 리포트 작성.", priority: "low", tag: "모니터링" },
  ],
  Legal: [
    { text: "유통사 계약서 수익배분 80% 독소조항 수정안 작성", desc: "스카이뮤직 표준계약서 6조 수익배분 조항 분석. 업계 평균(60~70%) 기준으로 수정 요청안 작성. A&R팀과 협의 후 협상 포지션 정리.", priority: "high", tag: "계약검토" },
    { text: "표절 시비 내용증명 수신 – 대응 서면 초안 작성", desc: "스타라이트 엔터로부터 수신한 내용증명 검토. 법적 근거 분석 후 반박 내용증명 초안 작성. A&R팀에 원작 작업 파일 확보 요청.", priority: "high", tag: "법적대응" },
    { text: "루나&스타즈 전속계약 갱신 조건 협상안 정리", desc: "현 계약 만료일(6개월 후) 대비 갱신 협상 개시. 수익배분 재조정·글로벌 활동 권리·계약기간 단축 요구안 정리 후 CEO 보고.", priority: "high", tag: "계약" },
    { text: "신곡 3편 저작권 출원 서류 문화재청 제출", desc: "이번 달 완성된 신곡 3편 저작권 등록 서류(악보·가사·녹음파일) 취합. 한국저작권위원회 온라인 출원 시스템 접수 후 접수증 보관.", priority: "medium", tag: "저작권" },
    { text: "광고 협찬 계약서 (서울전자) 표준약관 기준 검토", desc: "서울전자 광고 협찬 계약서 표준약관 적합성 검토. 제품 노출 방식·독점 기간·위약금 조항 확인 후 수정 의견 마케팅팀에 전달.", priority: "medium", tag: "계약검토" },
    { text: "계약서 데이터베이스 최신화 (Q1 계약분)", desc: "이번 분기 체결된 계약서 전체(유통·광고·외주·전속) 리스트 최신화. 만료일 임박 계약(3개월 이내) 별도 표시 후 CEO에게 보고.", priority: "low", tag: "관리" },
    { text: "직원 NDA 갱신 안내 공지 초안 작성", desc: "신규 입사자 및 NDA 만료 예정 직원 대상 갱신 안내 메일 초안 작성. 인사팀과 협조해 대상자 명단 확인 후 배포.", priority: "low", tag: "내부" },
  ],
};

const STORAGE_KEY_PREFIX = "kap_todos_v3_";

function buildKey(role: string): string {
  const wsId = localStorage.getItem("kap_solo_workspace_id") ?? "default";
  return `${STORAGE_KEY_PREFIX}${wsId}_${role}`;
}

function genId(): string {
  return `todo_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export function TodoProvider({ children }: { children: ReactNode }) {
  const { jobRole } = useRole();
  const storageKey = buildKey(jobRole);

  const load = (): Todo[] => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw) as Todo[];
    } catch (_e) {
      // ignore
    }
    const defaults = ROLE_TODOS[jobRole] ?? ROLE_TODOS["CEO"];
    return defaults.map((t) => ({ ...t, id: genId(), done: false, source: "role" as const }));
  };

  const [todos, setTodos] = useState<Todo[]>(load);

  useEffect(() => {
    setTodos(load());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobRole]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(todos));
  }, [todos, storageKey]);

  const toggle = useCallback((id: string) => {
    setTodos((prev) => prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));
  }, []);

  const addTodos = useCallback((items: Omit<Todo, "id" | "done">[]) => {
    const newItems: Todo[] = items.map((item) => ({ ...item, id: genId(), done: false }));
    setTodos((prev) => [...newItems, ...prev]);
  }, []);

  const clearDone = useCallback(() => {
    setTodos((prev) => prev.filter((t) => !t.done));
  }, []);

  return (
    <TodoContext.Provider value={{ todos, toggle, addTodos, clearDone }}>
      {children}
    </TodoContext.Provider>
  );
}

export function useTodoContext(): TodoContextValue {
  const ctx = useContext(TodoContext);
  if (!ctx) throw new Error("useTodoContext must be used within TodoProvider");
  return ctx;
}
